import { ChangeDetectorRef, inject } from '@angular/core';
import { take } from 'rxjs';

import { Entity } from '../objects/entity';
import { EntityType } from '../objects/interface-library';
import { DocumentViewerService, DocumentViewerResult } from '../service/document-viewer.service';
import { AttachmentsService } from '../service/files-and-attachments/attachments.service';
import { NewAttachmentComponent } from '../create-new/new-attachment/new-attachment.component';
import { NewCriticalDateComponent } from '../create-new/new-critical-date/new-critical-date.component';

/**
 * Base class for read-only entity view components
 * Provides common functionality for viewing documents, managing attachments, and critical dates
 */
export class ViewEntity extends Entity {
  protected documentViewerService = inject(DocumentViewerService);
  protected attachmentsService = inject(AttachmentsService);
  /** Zoneless app — a post-`await` field assignment must notify Angular explicitly. */
  protected cdrZoneless = inject(ChangeDetectorRef);
  // dialog and criticalDatesService are inherited from Entity as public properties

  // Document viewing
  showDocumentViewer = false;
  currentDocument: DocumentViewerResult | null = null;

  // User permissions
  userRole: string | null = null;

  // Entity data arrays
  entityAttachments: any[] = [];
  entityDates: any[] = [];

  // These came over from vendorComponent and should be deleted?
  // attachmentObjectArray = [];
  // dateObjectArray: CriticalDate[] = [];

  constructor() {
    super();
  }

  /**
   * Load and process a document for viewing
   */
  async loadDocument(fileUrlOrAttachment: string | any): Promise<void> {
    if (!fileUrlOrAttachment) {
      this.currentDocument = null;
      this.cdrZoneless.markForCheck();
      return;
    }
    this.currentDocument = await this.documentViewerService.processDocument(fileUrlOrAttachment);
    this.cdrZoneless.markForCheck();   // zoneless: post-await assignment needs an explicit notify
  }

  /**
   * Toggle document viewer visibility
   */
  toggleDocumentViewer(): void {
    this.showDocumentViewer = !this.showDocumentViewer;
  }

  /**
   * Download current document
   */
  downloadDocument(): void {
    if (this.currentDocument) {
      // fetchUrl, not directLink — the bucket is private, so only a presigned url resolves.
      this.documentViewerService.downloadFile(
        this.currentDocument.fetchUrl,
        this.currentDocument.fileName
      );
    }
  }

  /**
   * Open current document in new tab
   */
  openDocumentInNewTab(): void {
    if (this.currentDocument) {
      this.documentViewerService.openInNewTab(this.currentDocument.fetchUrl);
    }
  }

  /**
   * Add an attachment to an entity
   * Subclasses must provide their own implementation or override this method
   *
   * @param entityObject - The entity object to attach to
   * @param entityIdField - The field name for the entity's ID (e.g., 'contractNum', 'vendorNum', 'baaNum')
   * @param updateMethod - The service method to call for partial updates
   */
  protected addAttachment(
    entityObject: any,
    entityIdField: string,
    updateMethod: (id: string, update: any) => any
  ): void {
    if (!entityObject?.entityType) {
      // Try to infer entity type from ID field
      if (entityIdField === 'contractNum') {
        entityObject.entityType = EntityType.Contract;
      } else if (entityIdField === 'vendorNum' || entityIdField === 'partyNum') {
        entityObject.entityType = EntityType.Party;
      } else if (entityIdField === 'baaNum') {
        entityObject.entityType = EntityType.Baa;
      } else if (entityIdField === 'coiNum') {
        entityObject.entityType = EntityType.Coi;
      } else if (entityIdField === 'contactNum') {
        entityObject.entityType = EntityType.Person;
      } else if (entityIdField === 'userNum') {
        entityObject.entityType = EntityType.User;
      }
    }

    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityObject: entityObject
      }
    });

    dialogRef.afterClosed().subscribe(newAttachment => {
      if (!entityObject) {
        console.error('Entity object is not available.');
        return;
      }

      if (newAttachment && newAttachment.attachmentNum) {
        const entityId = entityObject[entityIdField];
        if (!entityId) {
          console.error(`Cannot update entity: ${entityIdField} is missing.`);
          return;
        }

        // Handle attachments array
        const attachmentsArray = entityObject.attachments;

        if (!attachmentsArray || !Array.isArray(attachmentsArray)) {
          entityObject.attachments = [newAttachment.attachmentNum];
        } else {
          attachmentsArray.push(newAttachment.attachmentNum);
        }

        // Perform partial update
        updateMethod(entityId, { attachments: entityObject.attachments })
          .pipe(take(1))
          .subscribe({
            next: (updated: any) => {
              // Fetch and add the new attachment to display
              this.attachmentsService.getAttachmentsByNumbers([newAttachment.attachmentNum])
                .subscribe(newAttachments => {
                  if (newAttachments && newAttachments.length > 0) {
                    this.entityAttachments = [...this.entityAttachments, ...newAttachments];
                  }
                });
            },
            error: (err) => {
              console.error('Failed to update entity with new attachment:', err);
            }
          });
      }
    });
  }

  /**
   * Add a critical date to an entity
   * Subclasses must provide their own implementation or override this method
   *
   * @param entityObject - The entity object to add date to
   * @param entityIdField - The field name for the entity's ID (e.g., 'contractNum', 'vendorNum', 'baaNum')
   * @param updateMethod - The service method to call for partial updates
   */
  protected addCriticalDate(
    entityObject: any,
    entityIdField: string,
    updateMethod: (id: string, update: any) => any
  ): void {
    if (!entityObject?.entityType) {
      // Try to infer entity type from ID field
      if (entityIdField === 'contractNum') {
        entityObject.entityType = EntityType.Contract;
      } else if (entityIdField === 'vendorNum' || entityIdField === 'partyNum') {
        entityObject.entityType = EntityType.Party;
      } else if (entityIdField === 'baaNum') {
        entityObject.entityType = EntityType.Baa;
      } else if (entityIdField === 'coiNum') {
        entityObject.entityType = EntityType.Coi;
      } else if (entityIdField === 'contactNum') {
        entityObject.entityType = EntityType.Person;
      } else if (entityIdField === 'userNum') {
        entityObject.entityType = EntityType.User;
      }
    }

    const dialogRef = this.dialog.open(NewCriticalDateComponent, {
      width: '800px',
      disableClose: false,
      maxWidth: '90vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        entityObject: entityObject
      }
    });

    dialogRef.afterClosed().subscribe(newDate => {
      if (!entityObject) {
        console.error('Entity object is not available.');
        return;
      }

      // Backend returns criticalDateId; legacy code used criticalDateNum
      const newDateId = newDate?.criticalDateId || newDate?.criticalDateNum;
      if (newDate && newDateId) {
        const entityId = entityObject[entityIdField];
        if (!entityId) {
          console.error(`Cannot update entity: ${entityIdField} is missing.`);
          return;
        }

        // Handle critical dates array
        const dateArray = entityObject.criticalDates;

        if (!dateArray || !Array.isArray(dateArray)) {
          entityObject.criticalDates = [newDateId];
        } else {
          dateArray.push(newDateId);
        }

        // Show the saved date straight away, from the object the dialog returned. It
        // used to wait on the partial update AND a batch re-read, then assign without
        // notifying Angular — in this zoneless app that left the list stale until some
        // unrelated event re-rendered it, and either call failing (the re-read
        // swallows its errors into []) dropped the date silently.
        this.entityDates = [...this.entityDates, newDate];
        this.cdrZoneless.markForCheck();

        // Link the date to the entity so it is listed on the next load
        updateMethod(entityId, { criticalDates: entityObject.criticalDates })
          .pipe(take(1))
          .subscribe({
            error: (err: any) => {
              console.error('Failed to update entity with new date:', err);
            }
          });
      }
    });
  }

  /**
   * Build a URL for an entity detail page
   * Used for creating clickable links to related entities
   *
   * @param entityType - Type of entity ('contract', 'party', 'baa', 'coi', 'site', 'legalEntity')
   * @param entityId - The ID of the entity
   * @returns URL path or null if entityId is not provided
   */
  protected buildEntityUrl(entityType: string, entityId: string | null | undefined): string | null {
    if (!entityId) return null;

    const routes: { [key: string]: string } = {
      'contract': '/securehome/contract',
      'party': '/securehome/vendor',
      'vendor': '/securehome/vendor',
      'baa': '/securehome/baa',
      'coi': '/securehome/coi',
      'site': '/securehome/site',
      'legalEntity': '/securehome/legal-entity'
    };

    const basePath = routes[entityType];
    return basePath ? `${basePath}/${entityId}` : null;
  }

  /**
   * Extract entity ID from an entity value (could be object or string)
   * Handles hydrated objects, ID strings, and null/undefined
   *
   * @param value - The entity value (object with ID property, string ID, or null)
   * @param idField - The field name containing the ID (e.g., 'vendorNum', 'contractId', 'siteNum')
   * @returns The entity ID or null
   */
  protected extractEntityId(value: any, idField: string): string | null {
    if (!value) return null;

    // If it's already a string, return it
    if (typeof value === 'string') return value;

    // If it's an object, extract the ID field
    if (typeof value === 'object' && value[idField]) return value[idField];

    return null;
  }
}
